import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useEffect } from 'react';
import { Paper, Grid, Checkbox, FormGroup, FormControlLabel, Link } from '@material-ui/core';
import CustomTable from 'components/CustomTable';
import { useSelector, useDispatch } from 'react-redux';
import {
    UPDATE_APPLICATION_DETAIL_POST_DATA,
    UPDATE_REMOVE_REQUEST_DATA,
    UPDATE_FORM_VALIDITY,
    UPDATE_CLINICAL_STUDY_CATEGORIZATION,
} from 'store/key';
import MsgBox from 'components/MsgBox/MsgBox';
import { getSystemMessage } from 'store/system/systemMessageReducer';
import { colorMain } from 'constants/color';
import { isEqual } from 'lodash';
import usePrevious from 'hooks/usePrevious';
import TextHeader from 'components/CustomText/TextHeader';

const useStyles = makeStyles((theme) => ({
    paperMargin: {
        margin: 10,
    },
    gridPadding: {
        padding: 10,
    },
    formTitleWeight: {
        fontWeight: 'bold',
    },
    subTitle: {
        paddingBottom: theme.spacing(2),
    },
    subTitleItem: {
        paddingBottom: theme.spacing(1),
    },
    channelBox: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    channelItem: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
        borderBottom: '1px solid #dcdcdc',
    },
    channelTitle: {
        paddingRight: theme.spacing(2),
        whiteSpace: 'nowrap',
        fontWeight: 'bold',
    },
    channelDetail: {},
    btn: {
        marginRight: theme.spacing(2),
    },
}));

const ClinicalStudyCategorizationFormLayout = (props) => {
    const classes = useStyles();

    const { formId, disableOnPermission } = props;

    const [warningMsg, setWarningMsg] = useState(null);

    const dispatch = useDispatch();

    const role = useSelector((state) => state.user.role);
    const applicationDetail = useSelector((state) => state.applicationDetail[formId]);
    const applicationDetailPostData = useSelector((state) => state.applicationDetailPostData);
    const clinicalStudyCategorization = useSelector((state) => state.clinicalStudyCategorization);

    const { defaultRows, rows, channel, defaultResultList, mapResultList, formCompleted } = clinicalStudyCategorization;

    const applicationForm = applicationDetail?.applicationForm ?? {};

    const prevApplicationForm = usePrevious(applicationForm);

    const postApplicationForm = applicationDetailPostData.postData.applicationForm ?? {};
    const psotRemoveRequestApplicationForm = applicationDetailPostData.removeRequest.applicationForm ?? {};
    const psotRemoveRequestList = psotRemoveRequestApplicationForm[formId] ?? [];

    const workflow = postApplicationForm.workflow ?? applicationForm.workflow;
    const postWorkflow = postApplicationForm.workflow;

    const columns = [
        { field: 'riskGroup', headerName: 'Risk Group', width: 280 },
        { field: 'number', headerName: 'No.', width: 80 },
        { field: 'riskFactors', headerName: 'Risk Factors', width: 380 },
        {
            field: 'action',
            headerName: 'Yes / No',
            width: 280,
            renderCell: (params) => {
                return (
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disabled={params.row.disabled}
                                    color="primary"
                                    checked={params.row.action === 'Y'}
                                    onChange={(e) => handleCheckedChange(e, params.row, 'Y')}
                                    name="checkedY"
                                />
                            }
                            label="Yes"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disabled={params.row.disabled}
                                    color="primary"
                                    checked={params.row.action === 'N'}
                                    onChange={(e) => handleCheckedChange(e, params.row, 'N')}
                                    name="checkedN"
                                />
                            }
                            label="No"
                        />
                    </FormGroup>
                );
            },
        },
    ];

    useEffect(() => {
        /** rows's step will change when workflow='N' (NTEC) */
        if (workflow === 'N') {
            dispatch({
                type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                payload: {
                    mapResultList: {
                        ...mapResultList,
                        6: {
                            ...mapResultList[6],
                            N: {
                                next: 7,
                                result: null,
                            },
                        },
                    },
                },
            });
        } else {
            dispatch({
                type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                payload: {
                    mapResultList: defaultResultList,
                },
            });
        }

        if (postWorkflow) {
            const newRows = resetValues(-1, { ...postApplicationForm }, rows, 0);
            dispatch({
                type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                payload: {
                    rows: newRows,
                },
            });
        }

        // eslint-disable-next-line
    }, [workflow]);

    /** init rows's checked start */
    const initRows = (index = 0, copyRows) => {
        for (let i = index; i < copyRows.length; i++) {
            const cscResult = applicationForm[`csc${i + 1}`];
            const action = cscResult ? 'Y' : 'N';

            const currentResult = mapResultList[i][action].result;
            if (currentResult !== null && cscResult !== null) {
                dispatch({
                    type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                    payload: {
                        channel: currentResult,
                    },
                });
            }

            if (cscResult === null) {
                if (i) {
                    const prevCsc = applicationForm[`csc${i}`] ? 'Y' : 'N';

                    /** when pre next !== null */
                    const preNextItem = mapResultList[i - 1][prevCsc];
                    const preNext = preNextItem.next;
                    const remotePreNextResult = applicationForm[`csc${preNext + 1}`];

                    if (remotePreNextResult !== null && preNext !== null) {
                        initRows(preNext, copyRows);
                    }
                }
                if (disableOnPermission) {
                    copyRows[i].disabled = true;
                }
                break;
            }
            copyRows[i].action = action;
            copyRows[i].existInDB = true;
            copyRows[i].disabled = disableOnPermission ? true : false;

            const next = mapResultList[i][action].next;
            if (next) {
                copyRows[next].disabled = disableOnPermission ? true : false;
            }
        }
        return copyRows;
    };
    useEffect(() => {
        if (!isEqual(prevApplicationForm, applicationForm)) {
            if (!isEqual(defaultRows, rows)) return;
            let copyRows = JSON.parse(JSON.stringify(rows));
            copyRows = initRows(0, copyRows);
            dispatch({
                type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                payload: {
                    rows: copyRows,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationForm]);
    /** init rows's checked end */

    /** check valid for clinicalStudyCategorizationFormValid */
    useEffect(() => {
        const gotChannel = channel !== null;
        dispatch({
            type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
            payload: {
                formCompleted: gotChannel,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);
    useEffect(() => {
        dispatch({
            type: UPDATE_FORM_VALIDITY,
            payload: {
                type: 'study',
                applicationFormId: formId,
                formKey: 'clinicalStudyCategorizationFormValid',
                isValid: formCompleted,
            },
        });
        // eslint-disable-next-line
    }, [formCompleted]);
    /** end */

    const resetValues = (index, copyPostApplicationForm, copyRows, enableIndex) => {
        /** reset next disabled & value when value change */
        let copyPsotRemoveRequestList = [...psotRemoveRequestList];
        for (let i = 0; i < rows.length; i++) {
            const itemKey = `csc${i + 1}`;
            if (i > index) {
                const cscResult = applicationForm[itemKey];
                /** Delete item for postData */
                if (copyPostApplicationForm[itemKey] !== undefined) {
                    delete copyPostApplicationForm[itemKey];
                }
                /** Add removeRequest data to postData when reset data */
                if (cscResult !== null) {
                    if (
                        !copyPsotRemoveRequestList.includes(itemKey) &&
                        copyPostApplicationForm[itemKey] === undefined
                    ) {
                        copyPsotRemoveRequestList.push(itemKey);
                    }
                }
                if (copyRows[i].action !== null) {
                    copyRows[i].action = null;
                }

                if (!copyRows[i].disabled) {
                    copyRows[i].disabled = true;
                }
            } else {
                /** Delete removeRequest's item when re-checked item */
                if (copyPsotRemoveRequestList.includes(itemKey) && copyRows[i].action !== null) {
                    const index = copyPsotRemoveRequestList.findIndex((i) => i === itemKey);
                    copyPsotRemoveRequestList.splice(index, 1);
                }
            }
        }

        if (enableIndex !== undefined) {
            copyRows[enableIndex].disabled = false;
        }

        /** reset postData when different from prevent */
        if (JSON.stringify(psotRemoveRequestList) !== JSON.stringify(copyPsotRemoveRequestList)) {
            dispatch({
                type: UPDATE_REMOVE_REQUEST_DATA,
                payload: {
                    type: 'applicationForm',
                    data: copyPsotRemoveRequestList.length ? copyPsotRemoveRequestList : null,
                    applicationFormId: formId,
                },
            });
        }

        /** reset postData when different from prevent */
        if (JSON.stringify(postApplicationForm) !== JSON.stringify(copyPostApplicationForm)) {
            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: 'applicationForm',
                    data: copyPostApplicationForm,
                    applicationFormId: formId,
                },
            });
        }

        return copyRows;
    };

    const handleCheckedChange = (e, row, value) => {
        const index = rows.findIndex((i) => i.id === row.id);
        if (rows[index].action === value) return;
        let copyRows = JSON.parse(JSON.stringify(rows));
        copyRows[index].action = value;

        let copyPostApplicationForm = { ...postApplicationForm };
        /** change applicationForm's value for postData */
        copyPostApplicationForm[`csc${index + 1}`] = value === 'Y' ? true : false;

        const result = mapResultList[index];
        copyRows = resetValues(index, copyPostApplicationForm, copyRows);

        if (result[value].next) {
            /** set disabled/enable if has next step */
            copyRows[result[value].next].disabled = false;
            dispatch({
                type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                payload: {
                    channel: null,
                },
            });
        } else {
            /** get result if has not next step */
            const channel = result[value].result;
            dispatch({
                type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
                payload: {
                    channel: channel,
                },
            });
            setWarningMsg({
                message: getSystemMessage('gotChannel', [channel]),
                footerActions: [
                    {
                        label: 'Confirm',
                        clicked: () => {
                            setWarningMsg(null);
                        },
                    },
                ],
            });
        }
        dispatch({
            type: UPDATE_CLINICAL_STUDY_CATEGORIZATION,
            payload: {
                rows: copyRows,
            },
        });
    };

    const mapChannelContent = (channel) => {
        const contentText = {
            A: {
                title: 'Channel A:',
                text: 'Full review by Standard Panel',
                subText: '(unless otherwise determined by the IRB/REC according to the IRB/REC’s SOP)',
            },
            B: {
                title: 'Channel B:',
                text: 'Expedited review by Expedited Panel',
                subText:
                    '(unless otherwise determined by the IRB/REC according to the IRB/REC’s SOP or requested by the principal investigator for a full review)',
            },
            C: {
                title: 'Channel C:',
                text: 'Full review by Phase 1 Panel',
                subText: '(Recommend the principal investigator to collaborate with a study site under the jurisdiction of a research ethics committee with a specific review panel for phase 1 clinical trials)',
            },
        };
        return Object.keys(contentText).map((key) => {
            const item = contentText[key];
            /** highlight the result and only viewed by CCO, Central IRB, Secretary and Cluster Admin */
            const viewed = ['cco', 'irb', 'secretary', 'cluster-admin'].includes(role);
            const highlightStyle = { color: viewed && channel === key ? colorMain : '' };

            return (
                <Grid container className={classes.channelItem} key={key}>
                    <Grid className={classes.channelTitle} style={highlightStyle}>
                        {item.title}
                    </Grid>
                    <Grid container wrap="nowrap" className={classes.channelDetail} style={highlightStyle}>
                        <strong style={{ marginRight: 8 }}>{item.text}</strong>
                        {item.subText}
                    </Grid>
                </Grid>
            );
        });
    };

    const isHighlight = () => {
        let ishl = false;
        const changedItem = applicationDetail?.changedItem?.changedItem;
        for (let i = 0; i < rows.length; i++) {
            if (
                changedItem &&
                changedItem.applicationForm &&
                changedItem.applicationForm[`${formId}`] &&
                changedItem.applicationForm[`${formId}`].indexOf(`csc${i + 1}`) > -1
            ) {
                ishl = true;
                break;
            }
        }
        return ishl;
    };

    return (
        <div>
            {warningMsg ? (
                <MsgBox
                    type={warningMsg.type}
                    message={warningMsg.message}
                    footerActions={warningMsg.footerActions}
                    title={warningMsg.title}
                    closePopup={() => setWarningMsg(null)}
                />
            ) : null}
            <Paper className={classes.paperMargin}>
                <div className={classes.gridPadding}>
                    <TextHeader underline italic>
                        <span style={{ background: isHighlight() ? 'yellow' : 'initial' }}>
                            CLINICAL STUDY CATEGORIZATION FORM
                        </span>
                    </TextHeader>
                </div>
                <Grid container className={classes.gridPadding}>
                    <Grid container className={classes.subTitle}>
                        <Grid container className={classes.subTitleItem}>
                            Note to Investigator:
                        </Grid>
                        <Grid container className={classes.subTitleItem}>
                            Please complete the following Clinical Study Categorization Form and submit the Form
                            together with each application for research ethics review. Upon receipt of an application,
                            the Secretariat will verify the information on the form and arrange for appropriate initial
                            review through Full Review, Expedited Review or Full Review by Phase 1 Panel.
                        </Grid>
                    </Grid>
                    <Grid container>
                        <CustomTable
                            isSortable={false}
                            columns={columns}
                            rows={rows}
                            allowResize
                            fullWidthTable={false}
                            textAlign="left"
                            skipMaxHeight
                        />
                    </Grid>
                    <Grid container className={classes.channelBox}>
                        {mapChannelContent(channel)}
                    </Grid>
                    <Grid container>
                        <Grid container alignItems="center">
                            Click{' '}
                            <Link
                                style={{ margin: '0 4px', fontWeight: 'bold' }}
                                href={
                                    process.env.PUBLIC_URL +
                                    '/files/20210902_Notes For Completion of Clinical Categorization Form.pdf'
                                }
                                download={'NotesForCompletion'}
                                rel={'noopener noreferrer'}
                                target={'_blank'}
                            >
                                here
                            </Link>
                            for Notes for Completion of the Clinical Study Categorization Form
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
        </div>
    );
};

export default ClinicalStudyCategorizationFormLayout;
